import { renderCell, renderHeader } from '../components/table';
import { useMemo, useContext } from 'react';
import { Flex, Box, Center, Spinner } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { BreakdownBy } from '../components/breakdown-by';
import { useDispatch } from 'react-redux';

import { applyGroup } from '@/store/atsReports/hire';
import {
  EmptyDataWrapper,
  isReportTableEmpty,
} from '../components/empty-data-wrapper';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { useKFlags } from '@/hooks/use-k-flags';
import { useDrillDownHandler } from '../drilldown/use-drilldownHeader';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { BREAKDOWN_OPTIONS, GROUP_BY_OPTIONS } from './survey-table-constants';
import { EMPLOYMENT_TYPE_CONFIG } from '@/constants/requisition-constants';

export interface Option {
  label: string;
  id: number | string;
}

const formatSurveyType = (type: string) => {
  return EMPLOYMENT_TYPE_CONFIG[type]?.label || type;
};

export const SurveyTable = ({
  groupBy1 = undefined,
  groupBy2 = undefined,
  customData = undefined,
  isReadOnly = false,
  isPrintView = false,
  customBreakdownChange = undefined,
  renderTitle = undefined,
  onDrillDownOpen = (_data) => {},
  isStaticReport = false,
  onSortChange = (_sortState) => {},
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
  feReportsV3 = false,
  isFullScreen = false,
}) => {
  const dispatch = useDispatch();
  const { feDrilldown } = useKFlags();
  const { consumer } = useContext(ReportSelectionContext);

  const isSurveyTableLoading = false;
  const group = null;

  const dataToUse = customData;

  const getFieldWithData = (): string => {
    if (!dataToUse?.length) {
      return 'candidate_nps';
    }

    const npsFields = ['candidate_nps', 'hm_nps'];
    for (const field of npsFields) {
      if (dataToUse?.some((row) => row[field] > 0)) {
        return field;
      }
    }

    return 'candidate_nps';
  };

  const fieldToUse = getFieldWithData();
  const isDataEmpty = isReportTableEmpty(dataToUse, fieldToUse);
  const { handleDrillDown } = useDrillDownHandler(feDrilldown, onDrillDownOpen);
  const drilldownGroup = {
    group_by_1: feReportsV3 ? groupBy1 : group?.group_by_1,
    group_by_2: feReportsV3 ? groupBy2 : group?.group_by_2,
  };

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const columns = useMemo(() => {
    const header = feReportsV3
      ? groupBy1 || GROUP_BY_OPTIONS[0]
      : groupBy1 || group?.group_by_1 || GROUP_BY_OPTIONS[0];

    const headerColumn = renderHeader({
      accessor: 'header',
      title: header?.label || 'Name',
      width: 280,
      expand: false,
      sort: true,

      renderFooter: () => <KText size="sm-semi-bold">Total</KText>,
      isReadOnly,
      columnConfig: false,
    });

    const customHeaderColumn = {
      ...headerColumn,
      Cell: (props: any) => {
        const formattedValue = formatSurveyType(props.cell.value);

        const modifiedProps = {
          ...props,
          cell: {
            ...props.cell,
            value: formattedValue,
          },
        };

        return headerColumn.Cell(modifiedProps);
      },
      id: 'header',
      title: header?.label || 'Name',
      columnConfig: false,
      sortType: (rowA: any, rowB: any) => {
        const valueA = formatSurveyType(rowA?.values?.header) || '';
        const valueB = formatSurveyType(rowB?.values?.header) || '';

        return String(valueA)?.localeCompare(String(valueB));
      },
    };

    return [
      customHeaderColumn,
      {
        ...renderCell({
          title: 'Candidate NPS',
          accessor: 'candidate_nps',
          width: 150,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Candidate NPS',
                    false,
                    drilldownGroup,
                    null,
                    row?.candidate_nps
                  )
                }
              >
                {row?.candidate_nps ?? '-'}
              </Flex>
            );
          },
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'Candidate NPS',
                    true,
                    drilldownGroup,
                    null,
                    row?.candidate_nps_total
                  )
                }
              >
                <KText size="sm-semi-bold">
                  {row?.candidate_nps_total ?? '-'}
                </KText>
              </Flex>
            );
          },
        }),
        id: 'candidate_nps',
        title: 'Candidate NPS',
        columnConfig: true,
      },
      {
        ...renderCell({
          title: 'HM NPS',
          accessor: 'hm_nps',
          width: 150,
          sort: true,
          render: (_, row) => {
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'HM NPS',
                    false,
                    drilldownGroup,
                    null,
                    row?.hm_nps
                  )
                }
              >
                {row?.hm_nps ?? '-'}
              </Flex>
            );
          },
          renderFooter: (info) => {
            const row = info?.rows?.[0]?.original;
            return (
              <Flex
                _hover={{ textDecoration: 'underline' }}
                cursor={feDrilldown ? 'pointer' : 'default'}
                onClick={() =>
                  handleDrillDown(
                    row,
                    'HM NPS',
                    true,
                    drilldownGroup,
                    null,
                    row?.hm_nps_total
                  )
                }
              >
                <KText size="sm-semi-bold">{row?.hm_nps_total ?? '-'}</KText>
              </Flex>
            );
          },
        }),
        id: 'hm_nps',
        title: 'HM NPS',
        columnConfig: true,
      },
    ];
  }, [groupBy1, groupBy2, feReportsV3, group]); //eslint-disable-line

  // Single onChange handler
  const handleBreakdownChange = (key: string, option: Option) => {
    switch (key) {
      case 'group_by_1':
        dispatch(applyGroup({ key: 'group_by_1', value: option }));

        break;
      case 'group_by_2':
        dispatch(applyGroup({ key: 'group_by_2', value: option }));
        break;
      default:
        break;
    }
  };

  const handleSortChange = (state) => {
    const { sortBy } = state;
    const currentSort = sortBy?.[0];
    onSortChange(currentSort);
  };

  // Configuration array for BreakdownBy components - conditional Redux fallbacks
  const breakdownConfig = [
    {
      key: 'group_by_1',
      prefix: 'Group by',
      options: GROUP_BY_OPTIONS,
      selected: feReportsV3
        ? groupBy1 || GROUP_BY_OPTIONS[0]
        : groupBy1 || group?.group_by_1 || GROUP_BY_OPTIONS[0],
    },
    {
      key: 'group_by_2',
      prefix: 'Breakdown by',
      options: BREAKDOWN_OPTIONS,
      selected: feReportsV3
        ? groupBy2 || BREAKDOWN_OPTIONS[0]
        : groupBy2 || group?.group_by_2 || BREAKDOWN_OPTIONS[0],
    },
  ];

  return (
    <Wrapper role="group" isReadOnly={isReadOnly || isStaticReport}>
      {consumer === 'DEFAULT' && (
        <Flex
          alignItems="flex-start"
          justifyContent="space-between"
          position="sticky"
          top="0"
          zIndex="6"
          bg="white"
        >
          {renderTitle ? (
            renderTitle?.()
          ) : (
            <Box flex="1">
              <TitleWithTooltip
                label="NPS breakdown"
                metricDefinition="Comprehensive breakdown of the aggregate NPS data"
              />
            </Box>
          )}
          <Flex
            gap="8px"
            mr={
              isPrintView
                ? '0'
                : isDataEmpty
                  ? '15px'
                  : isEditMode
                    ? '55px'
                    : '35px'
            }
            mb="12px"
          >
            {breakdownConfig.map(({ key, prefix, options, selected }) => (
              <BreakdownBy
                key={key}
                isPortal={!isFullScreen}
                prefix={prefix}
                options={options}
                selected={selected}
                isEditMode={isEditMode}
                onChange={(option: Option) =>
                  customBreakdownChange
                    ? customBreakdownChange(key, option)
                    : handleBreakdownChange(key, option)
                }
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isSurveyTableLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper data={dataToUse} field={fieldToUse}>
          <Table
            tableId="survey-table"
            skipLocalStorage={skipLocalStorage}
            isColumnLevelSorting
            enableColumnConfig={isEditMode}
            columns={columns}
            data={dataToUse}
            enableFooter={true}
            tableColStyles={tableColStyles}
            defaultSortBy={[{ id: 'header', desc: false }]}
            layoutScrollContainerStyles="c-data-table wide-content table-container"
            enableExpanded={true}
            layoutStyles={{
              borderBottom: 'none',
            }}
            getTableState={handleSortChange}
            onTableColDataChange={onTableColDataChange}
            initialTableColData={initialTableColData}
          />
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
